/**
 * Tiny pub/sub for the `X-Consent-Outdated` server header.
 *
 * The api.client response interceptor publishes the most recent
 * outdated-scope list seen on any authenticated response. The
 * ConsentReacceptanceProvider subscribes and renders a hard-blocking
 * modal whenever the list is non-empty.
 *
 * Why a custom event-bus rather than React context — the api.client lives
 * outside the React tree and its response interceptor cannot dispatch
 * directly into a context. A module-level publisher is the simplest bridge.
 */

import type { RequiredConsentScope } from '@/constants/legal';

type Listener = (outdated: RequiredConsentScope[]) => void;

let _outdated: RequiredConsentScope[] = [];
const _listeners = new Set<Listener>();

const KNOWN_SCOPES: RequiredConsentScope[] = ['privacy_policy', 'terms_of_service', 'age_18_plus'];

function isKnownScope(value: string): value is RequiredConsentScope {
  return (KNOWN_SCOPES as string[]).includes(value);
}

/**
 * Set the outdated-scope list and notify subscribers.
 *
 * Called from `apiClient.interceptors.response`. Pass an empty array
 * to clear the modal (e.g., after a successful re-acceptance flow).
 */
export function publishConsentOutdated(value: string | string[] | undefined): void {
  if (!value) {
    if (_outdated.length === 0) return;
    _outdated = [];
    _listeners.forEach((fn) => fn(_outdated));
    return;
  }

  const raw = Array.isArray(value) ? value : value.split(',');
  const next = raw.map((s) => s.trim()).filter((s): s is RequiredConsentScope => isKnownScope(s));

  if (next.length === _outdated.length && next.every((scope, i) => scope === _outdated[i])) {
    return;
  }

  _outdated = next;
  _listeners.forEach((fn) => fn(_outdated));
}

export function subscribeConsentOutdated(listener: Listener): () => void {
  _listeners.add(listener);
  // Synchronously emit the current state so a late subscriber doesn't
  // miss the initial publication.
  listener(_outdated);
  return () => {
    _listeners.delete(listener);
  };
}

export function getCurrentOutdated(): RequiredConsentScope[] {
  return _outdated;
}
